<template>
  <div class="container mx-auto px-4 py-8 max-w-7xl">
    <div v-if="error && !isLoading" class="max-w-4xl mx-auto text-center py-12">
      <p class="text-red-500 mb-2">{{ $t('availability.errorLoading') }}</p>
      <p class="text-gray-500 text-sm">{{ error }}</p>
    </div>

    <div v-else-if="isEmptyAvailability" class="max-w-4xl mx-auto">
      <div class="rounded-lg bg-gray-50 p-8 text-center text-gray-600">
        {{ $t('generic.emptyPage') }}
      </div>
    </div>

    <div v-else class="max-w-7xl mx-auto">
      <h1 class="text-3xl font-bold text-slate-800 mb-2">{{ $t('availability.title') }}</h1>
      <p v-if="minimumBookingDurationMonths != null" class="text-sm text-slate-600 mb-4">
        {{ $t('availability.minimumDurationNotice', { n: minimumBookingDurationMonths }) }}
      </p>

      <!-- Content by view -->
      <div>
        <p class="text-sm text-slate-500 mb-4">{{ $t('availability.selectHint') }}</p>

        <div
          v-if="rooms.length > 0"
          class="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600"
        >
          <span class="font-medium text-slate-700">{{ $t('availability.legend') }}</span>
          <span class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-8 shrink-0 rounded bg-amber-400/90" aria-hidden="true"></span>
            {{ $t('availability.legendBooked') }}
          </span>
          <span class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-8 shrink-0 rounded bg-slate-400/90" aria-hidden="true"></span>
            {{ $t('availability.legendMaintenance') }}
          </span>
        </div>
        
        <!-- Loading State -->
        <div v-if="isLoading && rooms.length === 0" class="flex justify-center items-center py-12">
          <svg class="animate-spin h-8 w-8 text-sky-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <!-- Table view: rooms × months -->
        <div v-else-if="rooms.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full border-separate border-spacing-0">
              <thead class="bg-slate-50/95 sticky top-0 z-10 backdrop-blur-sm">
                <tr class="bg-slate-100/90">
                  <th
                    rowspan="2"
                    class="sticky left-0 z-40 bg-slate-100 border-b-2 border-r border-slate-200 px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider min-w-[128px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]"
                  >
                    {{ $t('availability.room') }}
                  </th>
                  <th
                    v-for="(group, groupIdx) in calendarYearGroups"
                    :key="group.year"
                    :colspan="group.count"
                    class="border-b-2 border-slate-200 px-2 py-2 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider"
                    :class="groupIdx !== calendarYearGroups.length - 1 ? 'border-r-2 border-slate-300' : 'border-r border-slate-200'"
                  >
                    {{ group.year }}
                  </th>
                </tr>
                <tr class="bg-slate-50/80">
                  <th
                    v-for="month in calendarMonths"
                    :key="month.key"
                    class="border-b border-slate-200 px-1 py-1 text-center text-xs font-medium text-slate-600 tracking-wider min-w-[36px]"
                    :class="month.month === 12 ? 'border-r-2 border-r-slate-300' : 'border-r border-slate-100'"
                  >
                    {{ month.monthName }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-slate-100">
                <tr v-for="room in rooms" :key="room._id" class="group hover:bg-slate-50/60 transition-colors">
                  <td
                    class="sticky left-0 z-30 bg-white border-r border-slate-200 px-4 py-2 whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] transition-colors"
                    :class="{ '!bg-slate-50/90': selection?.roomId === room._id }"
                  >
                    <div class="flex flex-col">
                      <span class="font-semibold text-slate-800">{{ room.roomNumber }}</span>
                      <span
                        v-if="room.isOnMaintenance"
                        class="mt-0.5 inline-flex w-fit px-2 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700"
                      >
                        {{ $t('availability.legendMaintenance') }}
                      </span>
                      <span
                        v-else-if="room.monthlyCost != null"
                        class="text-xs text-slate-500"
                      >{{ formatCurrencyAmount(room.monthlyCost, locale.value) }}/{{ $t('availability.month') }}</span>
                      <!--button
                        @click="handleReserve(room)"
                        :disabled="room.status !== 'available'"
                        class="mt-2 px-3 py-1 text-xs rounded-lg transition"
                        :class="room.status === 'available' 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
                      >
                        {{ $t('availability.reserve') }}
                      </button-->
                    </div>
                  </td>
                  <td
                    v-for="(month, monthIndex) in calendarMonths"
                    :key="`${room._id}-${month.key}`"
                    class="px-0 py-2 relative select-none transition-colors"
                    :class="[
                      month.month === 12 ? 'border-r-2 border-slate-300' : 'border-r border-slate-100',
                      isCellMaintenance(room, month)
                        ? 'cursor-default'
                        : isCellFree(room, month)
                          ? 'cursor-pointer'
                          : 'cursor-default',
                      !isCellMaintenance(room, month) && isCellInSelection(room._id, monthIndex)
                        ? 'bg-sky-200/90'
                        : '',
                      !isCellMaintenance(room, month) && isCellFree(room, month) && !isCellInSelection(room._id, monthIndex)
                        ? 'hover:bg-sky-100/80'
                        : '',
                      !isCellMaintenance(room, month) && isCellFree(room, month) && isCellInSelection(room._id, monthIndex)
                        ? 'hover:bg-sky-300/90'
                        : ''
                    ]"
                    @click="handleCellClick(room, monthIndex)"
                  >
                    <div class="relative h-10 flex items-center justify-center overflow-visible pointer-events-none">
                      <div
                        v-if="isCellMaintenance(room, month)"
                        class="absolute top-1/2 -translate-y-1/2 h-4 z-10 pointer-events-none"
                        :class="getMaintenanceBarClass(room, monthIndex)"
                        :style="getMaintenanceBarStyle()"
                        :title="$t('availability.maintenanceMonth')"
                      ></div>
                      <template v-else>
                        <div
                          v-for="(booking, idx) in getBookingsForMonth(room, month)"
                          :key="idx"
                          class="absolute top-1/2 -translate-y-1/2 h-4 z-0 pointer-events-auto"
                          :class="getBookingBarClass(booking, month, room._id)"
                          :style="getBookingBarStyle(booking, month)"
                          :title="getBookingTooltip(booking)"
                        ></div>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Selection bar (when a date range is selected) -->
      <div
        v-if="selection"
        class="mt-4 p-4 bg-sky-50/90 border border-sky-200 rounded-xl flex flex-wrap items-center justify-between gap-3 shadow-sm"
      >
        <div class="text-sm text-slate-700">
          <span class="font-medium">{{ $t('availability.room') }} {{ selection.room?.roomNumber }}</span>
          <template v-if="selectedPeriod">
            <span class="mx-2">·</span>
            <span>{{ formatContractDate(selectedPeriod.checkIn) }} – {{ formatContractDate(selectedPeriod.checkOut) }}</span>
          </template>
          <span v-if="selectionDurationMonths != null" class="mx-2">·</span>
          <span v-if="selectionDurationMonths != null">{{ selectionDurationMonths }} {{ $t('availability.month') }}</span>
        </div>
        <p v-if="selectionTooShort" class="w-full text-sm text-red-700">
          {{ $t('availability.minimumDurationTooShort', { n: minimumBookingDurationMonths }) }}
        </p>
        <p v-else-if="selectionTooLong" class="w-full text-sm text-red-700">
          {{ $t('availability.maximumDurationTooLong', { n: maximumBookingDurationMonths }) }}
        </p>
        <div class="flex gap-2">
          <button
            type="button"
            @click="reserveSelectedPeriod"
            :disabled="selectionInvalid"
            class="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ $t('availability.reserve') }} {{ $t('availability.selectedPeriod') }}
          </button>
          <button
            type="button"
            @click="clearSelection"
            class="inline-flex items-center gap-2 px-4 py-2 text-slate-700 bg-white border border-slate-300 text-sm font-medium rounded-lg hover:bg-slate-50 transition"
          >
            <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 18 6M6 6l12 12" />
            </svg>
            {{ $t('availability.clearSelection') }}
          </button>
        </div>
      </div>

      <!-- Reservation Modal (only when a room is selected) -->
      <RoomReservationModal
        v-if="selectedRoom"
        :isOpen="showReservationModal"
        :room="selectedRoom"
        :checkIn="initialCheckInRef"
        :checkOut="initialCheckOutRef"
        :minimum-booking-duration-months="minimumBookingDurationMonths"
        :maximum-booking-duration-months="maximumBookingDurationMonths"
        defaultContactMode="anonymous"
        @close="handleCloseReservationModal"
        @success="handleReservationSuccess"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '../composables/useApi';
import { formatCurrencyAmount } from '../utils/formatCurrency';
import { formatContractDate } from '../utils/formatDate';
import { inclusiveBookingMonths, meetsMinimumBookingDuration, meetsMaximumBookingDuration } from '../utils/bookingDuration';
import RoomReservationModal from '../components/rooms/RoomReservationModal.vue';
import { GLOBAL_SETTINGS_DEFAULTS } from '@merlot/common/globalSettingsDefaults.js';
import { isMonthInMaintenance } from '@merlot/common/roomPublicStatus.js';
import { buildAvailabilityCalendarMonths, selectedPeriodIso } from '@merlot/common/bookingMonthDates.js';

const { t, locale } = useI18n();
const api = useApi();

const rooms = ref([]);
const isLoading = ref(true);
const error = ref(null);

const isEmptyAvailability = computed(
  () => !isLoading.value && !error.value && rooms.value.length === 0
);

const minimumBookingDurationMonths = ref(GLOBAL_SETTINGS_DEFAULTS.minimumBookingDurationMonths);
const maximumBookingDurationMonths = ref(GLOBAL_SETTINGS_DEFAULTS.maximumBookingDurationMonths);
const availabilityCalendarMonths = ref(GLOBAL_SETTINGS_DEFAULTS.availabilityCalendarMonths);

const showReservationModal = ref(false);
const selectedRoom = ref(null);
const initialCheckInRef = ref(null);
const initialCheckOutRef = ref(null);
/** @type {import('vue').Ref<{ roomId: string, room: object, startIndex: number, endIndex: number } | null>} */
const selection = ref(null);

// View mode: 'table' | 'vertical' | 'period'
//const activeView = ref('table');
// Vertical view style: 'bar' (accent) | 'edge' (edge treatment) | 'shape' (shape language)
//const verticalStyle = ref('bar');

// Period view: month/year range
const today = new Date();
const currentMonth = today.getMonth() + 1;
const currentYear = today.getFullYear();
/*const monthOptions = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' }
];*/
//const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear + i);
const periodStartMonth = ref(currentMonth);
const periodStartYear = ref(currentYear);
const periodEndMonth = ref(currentMonth);
const periodEndYear = ref(currentYear);

// Period as UTC dates (start of first month, end of last month)
const periodStartDate = computed(() => new Date(Date.UTC(periodStartYear.value, periodStartMonth.value - 1, 1)));
const periodEndDate = computed(() => {
  const d = new Date(Date.UTC(periodEndYear.value, periodEndMonth.value, 0, 23, 59, 59, 999));
  return d;
});

/*function isRoomAvailableForPeriod(room) {
  const start = periodStartDate.value.getTime();
  const end = periodEndDate.value.getTime();
  if (!room.approvedBookings || !room.approvedBookings.length) return true;
  for (const b of room.approvedBookings) {
    if (!b.startDate || !b.endDate) continue;
    const bStart = new Date(b.startDate).getTime();
    const bEnd = new Date(b.endDate).getTime();
    if (bStart <= end && bEnd >= start) return false;
  }
  return true;
}*/

/*function handleReserveForPeriod(room) {
  const start = periodStartDate.value;
  const end = periodEndDate.value;
  const checkIn = `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const checkOut = `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`;
  handleReserve(room, checkIn, checkOut);
}*/

// Check if a month cell is under maintenance (hides bookings; blocks selection)
function isCellMaintenance(room, month) {
  return isMonthInMaintenance(room, month.year, month.month);
}

// Check if a month cell has no approved bookings (available for selection)
function isCellFree(room, month) {
  if (isCellMaintenance(room, month)) return false;
  return !getBookingsForMonth(room, month).length;
}

// Check if a month cell is part of the current selection for that room
function isCellInSelection(roomId, monthIndex) {
  if (!selection.value || selection.value.roomId !== roomId) return false;
  const lo = Math.min(selection.value.startIndex, selection.value.endIndex);
  const hi = Math.max(selection.value.startIndex, selection.value.endIndex);
  return monthIndex >= lo && monthIndex <= hi;
}

// Selection range as { startIndex, endIndex } (ordered)
function getSelectionRange() {
  if (!selection.value) return null;
  const start = Math.min(selection.value.startIndex, selection.value.endIndex);
  const end = Math.max(selection.value.startIndex, selection.value.endIndex);

  //console.log('getSelectionRange', start, end);

  return { start, end };
}

// Selected period as checkIn/checkOut strings (YYYY-MM-DD)
const selectedPeriod = computed(() => {
  const range = getSelectionRange();
  if (!range || !calendarMonths.value.length) return null;
  const startMonth = calendarMonths.value[range.start];
  const endMonth = calendarMonths.value[range.end];

  //console.log('selectedPeriod', startMonth, endMonth);

  if (!startMonth || !endMonth) return null;
  return selectedPeriodIso(calendarMonths.value, range.start, range.end);
});

const selectionDurationMonths = computed(() => {
  const period = selectedPeriod.value;
  if (!period?.checkIn || !period?.checkOut) return null;
  return inclusiveBookingMonths(period.checkIn, period.checkOut);
});

const selectionTooShort = computed(() => {
  const period = selectedPeriod.value;
  if (!period?.checkIn || !period?.checkOut) return false;
  return !meetsMinimumBookingDuration(
    period.checkIn,
    period.checkOut,
    minimumBookingDurationMonths.value
  );
});

const selectionTooLong = computed(() => {
  const period = selectedPeriod.value;
  if (!period?.checkIn || !period?.checkOut) return false;
  return !meetsMaximumBookingDuration(
    period.checkIn,
    period.checkOut,
    maximumBookingDurationMonths.value
  );
});

const selectionInvalid = computed(() => selectionTooShort.value || selectionTooLong.value);

function handleCellClick(room, monthIndex) {
  const month = calendarMonths.value[monthIndex];
  if (!month || isCellMaintenance(room, month)) return;
  if (!isCellFree(room, month)) return;
  //const current = selection.value;
  //console.log('handleCellClick', selection.value, room._id);

  if (selection.value && selection.value.roomId === room._id) {
    const hasStartIndex = selection.value.startIndex != null;
    const hasEndIndex = selection.value.endIndex != null;
    //if (!selection.value?.startIndex && !selection.value?.endIndex) {
    //  selection.value = { roomId: room._id, room, startIndex: monthIndex, endIndex: monthIndex };
   //   console.log('handleCellClick 1', selection.value);
    //  return;
    //}
    if (hasStartIndex && hasEndIndex && selection.value.startIndex < monthIndex) {
      selection.value = { roomId: room._id, room, startIndex: selection.value?.startIndex, endIndex: monthIndex };
      //console.log('handleCellClick 2', selection.value);
      return;
    }
    if (hasStartIndex && hasEndIndex && selection.value.startIndex > monthIndex) {
      selection.value = { roomId: room._id, room, startIndex: monthIndex, endIndex: monthIndex };
      //console.log('handleCellClick 3', selection.value);
      return;
    }
  } else {
    selection.value = { roomId: room._id, room, startIndex: monthIndex, endIndex: monthIndex };
    //console.log('handleCellClick 4', selection.value);
    return;
  }

  //selection.value = { roomId: room._id, room, startIndex: selection.value?.startIndex, endIndex: monthIndex };
  //console.log('handleCellClick 4', selection.value);
  /*if (!current || current.roomId !== room._id) {
    if (current?.startIndex < current?.endIndex) {
      selection.value = { ...current, endIndex: monthIndex };
    } else {
      selection.value = { roomId: room._id, room, startIndex: monthIndex, endIndex: monthIndex };
    }
    return;
  }*/
  //selection.value = { ...current, endIndex: monthIndex };
  //console.log('selection', selection.value);
}

function clearSelection() {
  selection.value = null;
}

function reserveSelectedPeriod() {
  const period = selectedPeriod.value;
  if (!period || !selection.value || selectionInvalid.value) return;
  const room = selection.value.room;
  const range = getSelectionRange();
  if (range && calendarMonths.value[range.start]) {
    const startMonth = calendarMonths.value[range.start];
    if (isCellMaintenance(room, startMonth)) return;
  }
  handleReserve(room, period.checkIn, period.checkOut);
  clearSelection();
}

// Generate calendar months from current UTC month
const calendarMonths = computed(() => {
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const now = new Date();
  return buildAvailabilityCalendarMonths(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    availabilityCalendarMonths.value
  ).map((m) => ({
    ...m,
    monthName: monthNames[m.month - 1]
  }));
});

// Group months by year for header row
const calendarYearGroups = computed(() => {
  const groups = [];
  for (const m of calendarMonths.value) {
    const last = groups.length > 0 ? groups[groups.length - 1] : null;
    if (!last || last.year !== m.year) {
      groups.push({ year: m.year, count: 1 });
    } else {
      last.count += 1;
    }
  }
  return groups;
});

// Get bookings that overlap with a specific month
function getBookingsForMonth(room, month) {
  if (!room.bookings || !Array.isArray(room.bookings)) {
    return [];
  }
  
  return room.bookings.filter(booking => {
    if (!booking.startDate || !booking.endDate) {
      return false;
    }
    
    const bookingStart = new Date(booking.startDate);
    const bookingEnd = new Date(booking.endDate);
    
    // Check if booking overlaps with month
    return bookingStart <= month.endDate && bookingEnd >= month.startDate;
  });
}

// Get booking bar style (position and width)
function getBookingBarStyle(booking, month) {
  const bookingStart = new Date(booking.startDate);
  const bookingEnd = new Date(booking.endDate);
  
  // Calculate position and width within the month cell
  const monthStart = month.startDate.getTime();
  const monthEnd = month.endDate.getTime();
  const bookingStartTime = bookingStart.getTime();
  const bookingEndTime = bookingEnd.getTime();
  
  // Calculate left position (percentage from start of month)
  const leftPercent = Math.max(0, ((bookingStartTime - monthStart) / (monthEnd - monthStart)) * 100);
  
  // Calculate width (percentage of month)
  const bookingStartInMonth = Math.max(monthStart, bookingStartTime);
  const bookingEndInMonth = Math.min(monthEnd, bookingEndTime);
  const widthPercent = ((bookingEndInMonth - bookingStartInMonth) / (monthEnd - monthStart)) * 100;
  
  return {
    left: `${leftPercent}%`,
    width: `${widthPercent}%`
  };
}

function getMaintenanceBarStyle() {
  return {
    left: '0%',
    width: '100%'
  };
}

function isMaintenanceMonthStart(room, monthIndex) {
  const month = calendarMonths.value[monthIndex];
  if (!month || !isCellMaintenance(room, month)) return false;
  if (monthIndex === 0) return true;
  const prev = calendarMonths.value[monthIndex - 1];
  return !isCellMaintenance(room, prev);
}

function isMaintenanceMonthEnd(room, monthIndex) {
  const month = calendarMonths.value[monthIndex];
  if (!month || !isCellMaintenance(room, month)) return false;
  if (monthIndex === calendarMonths.value.length - 1) return true;
  const next = calendarMonths.value[monthIndex + 1];
  return !isCellMaintenance(room, next);
}

function getMaintenanceBarClass(room, monthIndex) {
  const colorClass = 'bg-slate-400/90';
  const startsThisMonth = isMaintenanceMonthStart(room, monthIndex);
  const endsThisMonth = isMaintenanceMonthEnd(room, monthIndex);
  const leftRounded = startsThisMonth ? 'rounded-l' : '';
  const rightRounded = endsThisMonth ? 'rounded-r' : '';
  const middleSquare = !startsThisMonth && !endsThisMonth ? 'rounded-none' : '';
  return `${colorClass} ${middleSquare} ${leftRounded} ${rightRounded}`;
}

// Generate a consistent color for each room based on room ID
/*function getRoomColor(roomId) {
  // Use a hash of the room ID to generate a consistent color
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-yellow-500',
    'bg-indigo-500',
    'bg-red-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
    'bg-amber-500',
    'bg-emerald-500',
    'bg-violet-500',
    'bg-rose-500',
    'bg-sky-500'
  ];
  
  // Simple hash function to get consistent color for each room
  let hash = 0;
  const idStr = String(roomId);
  for (let i = 0; i < idStr.length; i++) {
    hash = ((hash << 5) - hash) + idStr.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}*/

// Get booking bar CSS class with room-specific color
function getBookingBarClass(booking, month, roomId) {
  const colorClass = 'bg-amber-400/90';
  const bookingStart = new Date(booking.startDate);
  const bookingEnd = new Date(booking.endDate);

  const startsThisMonth = bookingStart.getTime() >= month.startDate.getTime();
  const endsThisMonth = bookingEnd.getTime() <= month.endDate.getTime();

  const leftRounded = startsThisMonth ? 'rounded-l' : '';
  const rightRounded = endsThisMonth ? 'rounded-r' : '';
  const middleSquare = !startsThisMonth && !endsThisMonth ? 'rounded-none' : '';

  return `${colorClass} hover:opacity-100 cursor-pointer ${middleSquare} ${leftRounded} ${rightRounded}`;
}

// Get booking tooltip text
function getBookingTooltip(booking) {
  const start = new Date(booking.startDate);
  const end = new Date(booking.endDate);
  const startStr = start.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const endStr = end.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `Booked: ${startStr} - ${endStr}`;
}

// Fetch all rooms with availability
async function fetchRooms() {
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await api.get('/rooms/availability');
    
    if (!response || !response.data || !Array.isArray(response.data)) {
      // TODO: Handle this via sentry.io
      //console.error('Invalid API response:', response);
      rooms.value = [];
      return;
    }
    
    if (response?.settings) {
      if (response.settings.minimumBookingDurationMonths != null) {
        minimumBookingDurationMonths.value = response.settings.minimumBookingDurationMonths;
      }
      if (response.settings.maximumBookingDurationMonths != null) {
        maximumBookingDurationMonths.value = response.settings.maximumBookingDurationMonths;
      }
      if (response.settings.availabilityCalendarMonths != null) {
        availabilityCalendarMonths.value = response.settings.availabilityCalendarMonths;
      }
    }
    
    rooms.value = response.data.map(room => {
      const roomId = room._id ? (typeof room._id === 'string' ? room._id : room._id.toString()) : null;
      return {
        _id: roomId || room._id,
        roomNumber: room.roomNumber,
        monthlyCost: room.monthlyCost ?? null,
        publicStatus: room.publicStatus || 'available',
        maintenanceEndMonth: room.maintenanceEndMonth ?? null,
        isOnMaintenance: room.isOnMaintenance === true,
        bookings: room.bookings || []
      };
    });
  } catch (e) {
    // TODO: Handle this via sentry.io
    //console.error('Error fetching rooms:', e);
    error.value = e.message || t('availability.errorLoading');
  } finally {
    isLoading.value = false;
  }
}

// Handle reserve button click (optional initial dates from calendar selection)
function handleReserve(room, initialCheckIn = null, initialCheckOut = null) {
  //console.log('handleReserve', room, initialCheckIn, initialCheckOut);
  selectedRoom.value = room;
  initialCheckInRef.value = initialCheckIn;
  initialCheckOutRef.value = initialCheckOut;
  showReservationModal.value = true;
}

function handleCloseReservationModal() {
  showReservationModal.value = false;
  initialCheckInRef.value = null;
  initialCheckOutRef.value = null;
}

// Handle reservation success
function handleReservationSuccess() {
  fetchRooms();
}

// Clear selection on Escape
function onKeyDown(e) {
  if (e.key === 'Escape') clearSelection();
}

/*watch(initialCheckInRef, (newVal) => {
  if (newVal) {
    console.log('initialCheckInRef', newVal);
  }
});
watch(initialCheckOutRef, (newVal) => {
  if (newVal) {
    console.log('initialCheckOutRef', newVal);
  }
});*/
// Initialize
onMounted(() => {
  fetchRooms();
  window.addEventListener('keydown', onKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown);
});
</script>
